'use client';

import { DefaultLabel } from './DefaultLabel';
import { StackedLabel } from './StackedLabel';

/**
 * Entry props passed from Recharts Pie label renderer.
 * We model only the fields we actually use.
 */
export interface IPieChartLabelEntry {
  cx?: number;
  cy?: number;
  fill?: string;
  innerRadius?: number;
  midAngle?: number;
  name?: string;
  outerRadius?: number;
  payload?: { key?: string;[key: string]: unknown };
  percent?: number;
  value?: number;
}

export interface IPieChartLabelSmallValue {
  fill: string;
  key: string;
  name: string;
  value: number;
}

interface IPieChartLabelProps {
  entry: IPieChartLabelEntry;
  /**
   * Pre-calculated positions for small values (e.g. < 5%),
   * used to stack labels vertically and avoid overlap.
   */
  smallValuesPositions: IPieChartLabelSmallValue[];
  /**
   * Threshold (0–1) below which a slice is considered "small"
   * and rendered with leader lines instead of inside the slice.
   * Default: 0.05 (5%).
   */
  smallValueThreshold?: number;
}

/**
 * -----------------------------------------------------------------------------
 * Custom component to display the pie chart label.
 * Reusable pie chart label with leader lines for small values.
 *
 * Designed to be used as the `label` renderer for a Recharts `Pie`,
 * e.g. `label={(entry) => <PieChartLabel entry={entry} smallValuesPositions={...} />}`.
 */
export function PieChartLabel({
  entry,
  smallValuesPositions,
  smallValueThreshold = 0.05,
}: IPieChartLabelProps) {
  const {
    cx = 0,
    cy = 0,
    midAngle = 0,
    innerRadius = 0,
    outerRadius = 0,
    percent = 0,
    name = '',
    value = 0,
    fill = 'currentColor',
    payload,
  } = entry;

  // If value is large enough, use default label positioning inside/near the slice
  if (percent >= smallValueThreshold) {
    return (
      <DefaultLabel
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        midAngle={midAngle}
        name={name}
        outerRadius={outerRadius}
        percent={percent}
        smallValueThreshold={smallValueThreshold}
        value={value}
      />
    );
  }

  // For small values, always stack them vertically at the top-left
  // Find index in pre-calculated small values array
  return (
    <StackedLabel
      cx={cx}
      cy={cy}
      fill={fill}
      midAngle={midAngle}
      name={name}
      outerRadius={outerRadius}
      payload={payload}
      percent={percent}
      smallValueThreshold={smallValueThreshold}
      smallValuesPositions={smallValuesPositions}
      value={value}
    />
  );
}
